import React, { useEffect, useState } from 'react';
import { adminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  CheckCircle2, XCircle, RefreshCw, Loader2, ExternalLink, ImageOff, Wallet, Store,
} from 'lucide-react';
import { toast } from 'sonner';

const money = (amt, cur = 'CAD') => `${cur} ${Number(amt || 0).toFixed(2)}`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const KindIcon = ({ kind }) => (kind === 'goods'
  ? <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700"><Store className="h-3.5 w-3.5" /> Goods</span>
  : <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><Wallet className="h-3.5 w-3.5" /> ShipitNow</span>);

const ETransferPage = () => {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listETransferSubmissions(tab);
      setRows(data?.submissions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const noteFor = (id) => notes[id] || '';
  const setNote = (id, v) => setNotes((n) => ({ ...n, [id]: v }));

  const doApprove = async (id) => {
    setBusyId(id);
    try {
      const r = await adminApi.approveETransfer(id, noteFor(id));
      toast.success(`Approved. Order ${r.order_id} · ${r.invoice_kind} invoice marked paid${r.released_for_driver ? ' + released to drivers' : ''}.`);
      setRows((prev) => prev.filter((row) => row.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed');
    } finally { setBusyId(null); }
  };

  const doReject = async (id) => {
    if (!window.confirm('Reject this e-transfer submission?\n\nThe customer will need to re-submit.')) return;
    setBusyId(id);
    try {
      await adminApi.rejectETransfer(id, noteFor(id));
      toast.success('Rejected. Customer can re-submit.');
      setRows((prev) => prev.filter((row) => row.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reject failed');
    } finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">E-Transfer verifier</h1>
          <p className="text-slate-500 mt-1 max-w-2xl text-sm">
            Customers upload their Interac e-transfer confirmation screenshots when they pay a
            goods or fulfillment invoice by transfer. Approve to mark the invoice paid and release
            the order for driver assignment. Screenshots use short-lived signed URLs.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="etransfer-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-5">
        <TabsList data-testid="etransfer-tabs">
          <TabsTrigger value="pending" data-testid="etransfer-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="etransfer-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="etransfer-tab-rejected">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="flex items-center gap-2 p-8 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="etransfer-empty">
              No {tab} submissions.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {rows.map((r) => (
                <div key={r.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`etransfer-card-${r.id}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold" data-testid={`etransfer-order-${r.id}`}>
                          Order · {r.order_id}
                        </div>
                        <div className="text-base font-semibold text-slate-900 mt-0.5">
                          {r.order?.vendor_name || 'Vendor'} → {r.order?.customer_name || '—'}
                        </div>
                      </div>
                      <KindIcon kind={r.invoice_kind} />
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-sm">
                      <span className="text-slate-500">Amount</span>
                      <span className="font-semibold" data-testid={`etransfer-amount-${r.id}`}>
                        {money(r.amount_cad, 'CAD')}
                      </span>
                      {r.reference && (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="text-slate-500">Ref</span>
                          <code className="bg-slate-50 px-1.5 py-0.5 rounded text-slate-700 text-xs" data-testid={`etransfer-ref-${r.id}`}>
                            {r.reference}
                          </code>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Submitted {dt(r.submitted_at)}</div>
                  </div>
                  <div className="bg-slate-50 border-y border-slate-200">
                    {r.screenshot_url ? (
                      <a href={r.screenshot_url} target="_blank" rel="noreferrer" className="group block relative" data-testid={`etransfer-screenshot-${r.id}`}>
                        <img src={r.screenshot_url} alt="E-transfer confirmation" className="w-full max-h-72 object-contain bg-white" />
                        <div className="absolute top-2 right-2 bg-white/90 rounded-full px-2 py-1 text-xs text-slate-700 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                          <ExternalLink className="h-3 w-3" /> Open full-size
                        </div>
                      </a>
                    ) : (
                      <div className="p-6 flex items-center justify-center text-slate-400 text-xs gap-2">
                        <ImageOff className="h-4 w-4" /> Screenshot missing or expired
                      </div>
                    )}
                  </div>
                  {tab === 'pending' ? (
                    <div className="p-4 space-y-2">
                      <Input
                        placeholder="Internal note (optional)"
                        value={noteFor(r.id)}
                        onChange={(e) => setNote(r.id, e.target.value)}
                        data-testid={`etransfer-note-${r.id}`}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => doApprove(r.id)}
                          disabled={busyId === r.id}
                          data-testid={`etransfer-approve-${r.id}`}
                        >
                          {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                          onClick={() => doReject(r.id)}
                          disabled={busyId === r.id}
                          data-testid={`etransfer-reject-${r.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1.5" /> Reject
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 text-xs text-slate-500">
                      <div><b className="capitalize">{r.status}</b> · reviewed {dt(r.reviewed_at)}</div>
                      {r.review_note && <div className="mt-1 italic">&ldquo;{r.review_note}&rdquo;</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ETransferPage;
